import React, { useState } from 'react';
import { BackButton, Button } from '../ui';
import DiceBackground from '../DiceBackground';
import { useTranslation } from '../../i18n';

interface LegalProps {
  onBack: () => void;
}

type Tab = 'privacy' | 'terms';

export const Legal: React.FC<LegalProps> = ({ onBack }) => {
  const t = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('privacy');

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-start text-white p-4 pt-8 relative select-none bg-[#1a101f] overflow-hidden">
      <DiceBackground count={8} />

      <BackButton onClick={onBack} />

      <h1 className="text-xl lg:text-4xl font-bold tracking-tighter mb-4 text-theme drop-shadow-lg uppercase text-center relative z-10 mt-4">
        {t.legal}
      </h1>

      {/* Tab switcher */}
      <div className="flex gap-2 mb-4 relative z-10">
        <button
          onClick={() => setActiveTab('privacy')}
          className={`px-3 py-1.5 lg:px-6 lg:py-3 text-[8px] lg:text-xs font-bold uppercase tracking-wider pixel-border transition-all ${
            activeTab === 'privacy'
              ? 'bg-theme text-black'
              : 'bg-black/40 text-gray-500 hover:text-white'
          }`}
        >
          {t.privacyPolicy}
        </button>
        <button
          onClick={() => setActiveTab('terms')}
          className={`px-3 py-1.5 lg:px-6 lg:py-3 text-[8px] lg:text-xs font-bold uppercase tracking-wider pixel-border transition-all ${
            activeTab === 'terms'
              ? 'bg-theme text-black'
              : 'bg-black/40 text-gray-500 hover:text-white'
          }`}
        >
          {t.termsOfService}
        </button>
      </div>

      {/* Content */}
      <div className="w-full max-w-2xl flex-1 overflow-y-auto pr-2 custom-scrollbar relative z-10">
        {activeTab === 'privacy' ? <PrivacyPolicy /> : <TermsOfService />}
      </div>

    </div>
  );
};

// ─── Privacy Policy ──────────────────────────────────────────────────────────

const PrivacyPolicy: React.FC = () => {
  const t = useTranslation();
  return (
  <div className="flex flex-col gap-4">
    <Section title={t.privacyPolicy} accent="theme">
      <p>{t.legalLastUpdated}</p>
      <p>{t.privacyIntro}</p>
      <p>{t.privacyAgree}</p>
    </Section>

    <Section title={t.privacyS1Title} accent="blue">
      <h3 className="text-[8px] lg:text-xs text-blue-300 font-bold mt-2 mb-1">{t.privacyS1aTitle}</h3>
      <p>
        <strong className="text-white">Player Name:</strong> {t.privacyS1aName}
      </p>
      <p>
        <strong className="text-white">Email Address (optional):</strong> {t.privacyS1aEmail}
      </p>
      <p>
        <strong className="text-white">Chat Messages:</strong> {t.privacyS1aChat}
      </p>

      <h3 className="text-[8px] lg:text-xs text-blue-300 font-bold mt-2 mb-1">{t.privacyS1bTitle}</h3>
      <p>
        <strong className="text-white">IP Address:</strong> {t.privacyS1bIp}
      </p>
      <p>
        <strong className="text-white">Game Progress:</strong> {t.privacyS1bProgress}
      </p>
      <p>
        <strong className="text-white">Local Storage Data:</strong> {t.privacyS1bLocal}
      </p>
      <p>
        <strong className="text-white">Player ID:</strong> {t.privacyS1bPlayerId}
      </p>
      <p>
        <strong className="text-white">Error &amp; Performance Data (Sentry):</strong> {t.privacyS1bSentry}
      </p>
    </Section>

    <Section title={t.privacyS2Title} accent="green">
      <p>{t.privacyS2Intro}</p>
      {t.privacyS2Items.map((item, i) => <p key={i}>- {item}</p>)}
      <p>{t.privacyS2NoSell}</p>
    </Section>

    <Section title={t.privacyS2aTitle} accent="green">
      <p>{t.privacyS2aCookie1}</p>
      <p>{t.privacyS2aCookie2}</p>
      <p>{t.privacyS2aCookie3}</p>
      <p>{t.privacyS2aCookie4}</p>
    </Section>

    <Section title={t.privacyS3Title} accent="yellow">
      <p>{t.privacyS3Server}</p>
      <p>{t.privacyS3Client}</p>
      <p>{t.privacyS3Disclaimer}</p>
    </Section>

    <Section title={t.privacyS4Title} accent="purple">
      <p>{t.privacyS4Chat}</p>
      <p>{t.privacyS4Sentry}</p>
      <p>{t.privacyS4Local}</p>
    </Section>

    <Section title={t.privacyS5Title} accent="cyan">
      <p>{t.privacyS5Intro}</p>
      {t.privacyS5Items.map((item, i) => <p key={i}>- {item}</p>)}
      <p>{t.privacyS5Contact}</p>
      <p>{t.privacyS5Local}</p>
    </Section>

    <Section title={t.privacyS6Title} accent="red">
      <p>{t.privacyS6Text}</p>
    </Section>

    <Section title={t.privacyS7Title} accent="yellow">
      <p>{t.privacyS7Intro}</p>
      <p>
        <strong className="text-white">Supabase:</strong> Database and authentication.
        Subject to Supabase's privacy policy.
      </p>
      <p>
        <strong className="text-white">Cloudflare Pages:</strong> Website hosting and CDN.
        Subject to Cloudflare's privacy policy.
      </p>
      <p>
        <strong className="text-white">Sentry:</strong> Error tracking and application monitoring.
        Automatically collects error reports, browser/device information, and IP addresses when
        errors occur. Data is processed by Functional Software, Inc. (USA) and retained for up
        to 90 days. Subject to{' '}
        <a
          href="https://sentry.io/privacy/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-theme underline hover:text-purple-300"
        >
          Sentry's privacy policy
        </a>
        .
      </p>
      <p>
        <strong className="text-white">Google Analytics:</strong> Used only with your explicit
        consent. Collects anonymous usage data (page views, session duration, device/browser info)
        to help us improve the Service. Google Analytics cookies are loaded only after you accept
        the cookie consent banner. You can also opt out by installing the{' '}
        <a
          href="https://tools.google.com/dlpage/gaoptout"
          target="_blank"
          rel="noopener noreferrer"
          className="text-theme underline hover:text-purple-300"
        >
          Google Analytics Opt-out Browser Add-on
        </a>
        . Subject to Google's privacy policy.
      </p>
      <p>{t.privacyS7NoAds}</p>
    </Section>

    <Section title={t.privacyS8Title} accent="gray">
      <p>{t.privacyS8Text}</p>
    </Section>

    <Section title={t.privacyS9Title} accent="theme">
      <p>
        {t.privacyS9Text} <br />
        <strong className="text-theme">me@rizasabuncu.com</strong>
      </p>
    </Section>
  </div>
  );
};

// ─── Terms of Service ────────────────────────────────────────────────────────

const TermsOfService: React.FC = () => {
  const t = useTranslation();
  return (
  <div className="flex flex-col gap-4">
    <Section title={t.termsOfService} accent="theme">
      <p>{t.legalLastUpdated}</p>
      <p>{t.termsIntro}</p>
      <p>{t.termsAgree}</p>
    </Section>

    <Section title={t.termsS1Title} accent="blue">
      <p>{t.termsS1Text}</p>
    </Section>

    <Section title={t.termsS2Title} accent="green">
      <p>{t.termsS2Text1}</p>
      <p>{t.termsS2Text2}</p>
    </Section>

    <Section title={t.termsS3Title} accent="purple">
      <p>{t.termsS3Text1}</p>
      <p>{t.termsS3Text2}</p>
      <p>{t.termsS3Text3}</p>
    </Section>

    <Section title={t.termsS4Title} accent="yellow">
      <p>{t.termsS4Intro}</p>
      {t.termsS4Items.map((item, i) => <p key={i}>- {item}</p>)}
      <p>{t.termsS4Ban}</p>
    </Section>

    <Section title={t.termsS5Title} accent="red">
      <p>{t.termsS5Text1}</p>
      <p>{t.termsS5Text2}</p>
      <p>{t.termsS5Text3}</p>
    </Section>

    <Section title={t.termsS6Title} accent="cyan">
      <p>{t.termsS6Text1}</p>
      <p>{t.termsS6Text2}</p>
    </Section>

    <Section title={t.termsS7Title} accent="yellow">
      <p className="uppercase text-[8px] lg:text-[10px]">{t.termsS7Text1}</p>
      <p>{t.termsS7Text2}</p>
    </Section>

    <Section title={t.termsS8Title} accent="red">
      <p className="uppercase text-[8px] lg:text-[10px]">{t.termsS8Text1}</p>
      <p>{t.termsS8Text2}</p>
    </Section>

    <Section title={t.termsS9Title} accent="purple">
      <p>{t.termsS9Text1}</p>
      <p>{t.termsS9Text2}</p>
    </Section>

    <Section title={t.termsS10Title} accent="gray">
      <p>{t.termsS10Text}</p>
    </Section>

    <Section title={t.termsS11Title} accent="blue">
      <p>{t.termsS11Text}</p>
    </Section>

    <Section title={t.termsS12Title} accent="theme">
      <p>
        {t.termsS12Text} <br />
        <strong className="text-theme">me@rizasabuncu.com</strong>
      </p>
    </Section>
  </div>
  );
};

// ─── Reusable section component ──────────────────────────────────────────────

const ACCENT_COLORS: Record<string, { border: string; bg: string; text: string }> = {
  theme: { border: 'border-l-theme', bg: 'bg-purple-900/20', text: 'text-theme' },
  blue: { border: 'border-l-blue-400', bg: 'bg-blue-900/20', text: 'text-blue-400' },
  green: { border: 'border-l-green-400', bg: 'bg-green-900/20', text: 'text-green-400' },
  yellow: { border: 'border-l-yellow-400', bg: 'bg-yellow-900/20', text: 'text-yellow-400' },
  red: { border: 'border-l-red-400', bg: 'bg-red-900/20', text: 'text-red-400' },
  purple: { border: 'border-l-purple-400', bg: 'bg-purple-900/20', text: 'text-purple-400' },
  cyan: { border: 'border-l-cyan-400', bg: 'bg-cyan-900/20', text: 'text-cyan-400' },
  gray: { border: 'border-l-gray-400', bg: 'bg-gray-900/20', text: 'text-gray-400' },
};

interface SectionProps {
  title: string;
  accent: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, accent, children }) => {
  const colors = ACCENT_COLORS[accent] || ACCENT_COLORS.gray;
  return (
    <div className={`pixel-card p-3 lg:p-5 border-l-4 ${colors.border} ${colors.bg}`}>
      <h2 className={`${colors.text} text-[9px] lg:text-sm mb-2 uppercase font-bold`}>{title}</h2>
      <div className="text-[8px] lg:text-xs leading-relaxed text-gray-300 flex flex-col gap-1.5">
        {children}
      </div>
    </div>
  );
};
